/**
 * useBookingsList — data + filter/sort/search/pagination state for the
 * bookings list (BON-130). Split out of `BookingPage.tsx` to keep that file
 * under the repo's line cap; the page component stays a thin view.
 *
 * Any filter/search/sort change resets to page 1 (setOffset(0)); the pager
 * drives page changes. `refresh()` (BON-142) bumps an internal token that
 * re-triggers the list + KPI-stats fetch in place, without disturbing the
 * current filter/page — used to refresh after a CSR-created booking. As of
 * the global "New booking" launcher, the hook ALSO subscribes to
 * `bookingsRefreshBus` so a booking created from ANY entry point (palette,
 * "c then b", or this page's own CTA/FAB — they all route through the same
 * launcher now) refreshes this page whenever it's mounted, without the
 * launcher needing a direct reference to this hook's state.
 */
import { useEffect, useState } from 'react';

import {
  fetchBookingsList,
  type BookingListItem,
  type BookingSort,
  type BookingSortDir,
  type BookingStatusFilter,
} from '../../api/bookingsList';
import type { BookingSource } from '../../api/bookingsV2';
import { usePagination } from '../../hooks/usePagination';

import type { SourceValue, StatusValue } from './BookingFilters';
import { subscribeToBookingsRefresh } from './bookingsRefreshBus';
import { useBookingStats } from './useBookingStats';

/** Fixed page size — the app has no page-size selector (operator decision). */
export const PAGE_SIZE = 20;

interface ListQuery {
  status?: BookingStatusFilter;
  source?: BookingSource;
  q?: string;
  sort: BookingSort;
  dir: BookingSortDir;
  page: number;
}

/** Fetches one page of bookings for `query`, guarding against stale responses
 *  (AbortController). `onTotalCount` (a stable setter) lifts the full count to
 *  the caller so the pager can size itself. `refreshToken` re-triggers the
 *  fetch when bumped, without otherwise changing the query. */
function useBookingListFetch(
  query: ListQuery,
  onTotalCount: (n: number) => void,
  refreshToken: number,
) {
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { status, source, q, sort, dir, page } = query;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);
    fetchBookingsList({ status, source, q, sort, dir, page, pageSize: PAGE_SIZE }, { signal })
      .then((res) => {
        if (signal.aborted) return;
        setBookings(res.bookings);
        onTotalCount(res.totalCount);
      })
      .catch((err: unknown) => {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Failed to load bookings');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, source, q, sort, dir, page, onTotalCount, refreshToken]);

  return { bookings, loading, error };
}

export function useBookingsList() {
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatusState] = useState<StatusValue>('all');
  const [source, setSourceState] = useState<SourceValue>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<BookingSort>('customer');
  const [dir, setDir] = useState<BookingSortDir>('asc');

  const pager = usePagination(totalCount, PAGE_SIZE);
  const { setOffset } = pager;
  // Bumped after a CSR-created booking (BON-142) to refresh the list + KPI
  // strip in the background, without disturbing the current filter/page.
  const [refreshToken, setRefreshToken] = useState(0);
  const stats = useBookingStats(refreshToken);

  // Subscribe to the global refresh bus — the create-booking launcher (any
  // entry point: palette, "c then b", or this page's own CTA/FAB) notifies it
  // on a successful create.
  useEffect(() => subscribeToBookingsRefresh(() => setRefreshToken((t) => t + 1)), []);

  // Debounce search → committed query, resetting to page 1 on commit.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, setOffset]);

  const { bookings, loading, error } = useBookingListFetch(
    {
      status: status === 'all' ? undefined : status,
      source: source === 'all' ? undefined : source,
      q: search || undefined,
      sort,
      dir,
      page: pager.currentPage,
    },
    setTotalCount,
    refreshToken,
  );

  const resettingPage =
    <T>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setOffset(0);
    };
  const setStatus = resettingPage(setStatusState);
  const setSource = resettingPage(setSourceState);
  const changeSort = resettingPage(setSort);
  const toggleSort = (field: BookingSort) => {
    if (sort === field) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(field);
      setDir('asc');
    }
    setOffset(0);
  };
  const toggleDir = () => {
    setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    setOffset(0);
  };

  return {
    bookings,
    totalCount,
    stats,
    loading,
    error,
    status,
    setStatus,
    source,
    setSource,
    searchInput,
    setSearchInput,
    sort,
    setSort: changeSort,
    dir,
    toggleSort,
    toggleDir,
    pager,
    /** Refresh the list + KPI strip in place (BON-142 post-create). */
    refresh: () => setRefreshToken((t) => t + 1),
  };
}
